import { useTranslation } from 'react-i18next';

import { ColorName } from 'types';

import { useGetOrderStatsQuery } from 'redux/api/ordersApi';
import { useAppSelector } from 'redux/hooks';

import { routes } from 'data/routes';

import { Headline } from 'components/Headline';
import { OrderStatsTiles } from 'components/OrderStatsTiles';

import * as S from './OrderStats.styles';

/**
 * Inline KPI cards shown above the order list, in two rows:
 *   - "My sales" — the current salesperson's own orders.
 *   - "Zone"     — every order in their zone(s), matching the list below.
 * Both honour the same filters as the list (zone, season, marketing line from
 * the header settings + the customerCode search), so the numbers always
 * describe exactly the table underneath. The tiles themselves are the shared
 * OrderStatsTiles component, reused by the Statistics page; their accent
 * colour follows the selected warehouse (settings barColor).
 */
type OrderStatsProps = {
  /** Customer-code search currently applied to the list, mirrored into the stats. */
  customerCode?: string;
};

export function OrderStats({
  customerCode,
}: Readonly<OrderStatsProps>): React.ReactElement | null {
  const { t } = useTranslation();
  const { zone, season, marketLine, barColor } = useAppSelector((state) => state.settings);

  const { data, isLoading, isError } = useGetOrderStatsQuery(
    {
      customerCode: customerCode || undefined,
      zone: zone || undefined,
      season: season || undefined,
      marketingLine: marketLine || undefined,
    },
    {
      // Skip until the settings bar has hydrated from localStorage/userData so
      // we don't fire a param-less request on initial render before season is
      // populated, followed immediately by the real filtered request.
      skip: !season,
      // Refetch whenever the order list is (re)opened so the cards reflect the
      // latest order values — e.g. after editing item quantities, which only
      // invalidates the OrderHeader tag, not OrdersList.
      refetchOnMountOrArgChange: true,
    }
  );

  // Stats unavailable: never break the list page.
  if (isError) return null;

  return (
    <S.Wrapper>
      <S.Header>
        <Headline tag="h2" scale="xl" color="blackLight" fontWeight="normal">
          {t('statistics.myPerformance')}
        </Headline>

        <S.StatisticsLink to={routes.statistics}>
          {t('statistics.viewFull')} →
        </S.StatisticsLink>
      </S.Header>

      <OrderStatsTiles
        title={t('statistics.mySales')}
        data={data?.mine}
        isLoading={isLoading}
        headerColor={barColor as ColorName}
      />
      <OrderStatsTiles
        title={t('zone.label')}
        data={data?.zone}
        isLoading={isLoading}
        headerColor={barColor as ColorName}
      />
    </S.Wrapper>
  );
}
